import { useCallback, useEffect, useRef, useState } from 'react'
import {
    getCategories,
    searchCategories,
    type CategoriesResponse,
    type Category,
    type CategorySearchResponse
} from '../api/categoriesApi.ts'
import { DEFAULT_PAGE_SIZE } from '../config/api.ts'

export interface UseCategorySearchResult {
    /** The list of matching categories */
    readonly categories: Category[]
    /** Total number of categories matching the query (may exceed returned count) */
    readonly total: number
    /** Whether a search request is currently in flight */
    readonly isLoading: boolean
    /** Error message if the last request failed */
    readonly error: string | null
    /** Retry the last search */
    readonly retry: () => void
}

interface SearchState {
    categories: Prettify<Category>[]
    total: number
    isLoading: boolean
    error: string | null
}

const INITIAL_STATE = {
    categories: [],
    total: 0,
    isLoading: false,
    error: null
} as const satisfies SearchState

/**
 * Custom hook for searching categories via OCAPI.
 *
 * Handles:
 * - API calls with proper loading/error states
 * - Request cancellation to prevent race conditions
 * - Automatic retry capability
 * - Empty query handling (fetches all categories)
 *
 * @param catalogId - The catalog ID to search within
 * @param searchTerm - The debounced search term to query
 * @param pageSize - Maximum number of results to return (default: 100)
 */
export function useCategorySearch(
    catalogId: string,
    searchTerm: string,
    pageSize: number = DEFAULT_PAGE_SIZE
): UseCategorySearchResult {
    const [state, setState] = useState<SearchState>(INITIAL_STATE)
    const abortControllerRef = useRef<AbortController | null>(null)
    const lastSearchTermRef = useRef<string>('')

    const executeSearch = useCallback(
        async (query: string, signal: AbortSignal) => {
            try {
                let categories: Category[]
                let total: number

                if (query.trim()) {
                    // Perform text search (POST request)
                    const response = (await searchCategories(
                        catalogId,
                        query.trim(),
                        {
                            count: pageSize
                        },
                        signal
                    )) satisfies CategorySearchResponse
                    categories = response.hits ?? []
                    total = response.total
                } else {
                    // Fetch all categories when no search term
                    const response = (await getCategories(catalogId, {
                        count: pageSize
                    })) satisfies CategoriesResponse
                    categories = response.data
                    total = response.total
                }

                // Only update state if this request wasn't aborted
                if (!signal.aborted) {
                    setState({
                        categories,
                        total,
                        isLoading: false,
                        error: null
                    })
                }
            } catch (err) {
                // Ignore abort errors - they're expected when canceling requests
                if (err instanceof Error && err.name === 'AbortError') return

                // Only update error state if not aborted
                if (!signal.aborted) {
                    const errorMessage =
                        err instanceof Error
                            ? err.message
                            : 'An unexpected error occurred while searching categories'

                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        error: errorMessage
                    }))
                }
            }
        },
        [catalogId, pageSize]
    )

    const onSearchTermChange = useCallback(
        (query: string) => {
            // Cancel any in-flight request
            if (abortControllerRef.current) abortControllerRef.current.abort()

            // Create new abort controller for this request
            const controller = new AbortController()
            abortControllerRef.current = controller
            lastSearchTermRef.current = query

            // Set loading state
            setState((prev) => ({
                ...prev,
                isLoading: true,
                error: null
            }))

            // Execute the search
            void executeSearch(query, controller.signal)
        },
        [executeSearch]
    )

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- This effect intentionally triggers a network request and sets loading/error state.
        onSearchTermChange(searchTerm)
        return () => abortControllerRef.current?.abort()
    }, [catalogId, searchTerm, onSearchTermChange])

    // Retry function to re-execute the last search
    const retry = useCallback(() => {
        onSearchTermChange(lastSearchTermRef.current)
    }, [onSearchTermChange])

    return {
        categories: state.categories,
        total: state.total,
        isLoading: state.isLoading,
        error: state.error,
        retry
    }
}
